import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AuditLogTable from './components/AuditLogTable';
import AuditStatistics from './components/AuditStatistics';
import SecurityEventsPanel from './components/SecurityEventsPanel';
import AdminActionsPanel from './components/AdminActionsPanel';
import FailedPaymentsPanel from './components/FailedPaymentsPanel';
import SystemChangesPanel from './components/SystemChangesPanel';
import { auditTrailService } from '../../lib/auditTrailService';

const SystemAuditTrailDashboard = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [auditLogs, setAuditLogs] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    eventType: null,
    severity: null,
    startDate: null,
    endDate: null
  });

  useEffect(() => {
    loadAuditData();
  }, [activeTab, filters]);

  const loadAuditData = async () => {
    setLoading(true);
    try {
      const { data: stats } = await auditTrailService?.getAuditStatistics(null, 30);
      setStatistics(stats);

      const { data: logs } = await auditTrailService?.getAuditLogs({
        ...filters,
        limit: 100
      });
      setAuditLogs(logs || []);
    } catch (error) {
      console.error('Error loading audit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    const { data: csvData } = await auditTrailService?.exportAuditLogs(filters);
    if (csvData) {
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL?.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date()?.toISOString()}.csv`;
      a?.click();
    }
  };

  const tabs = [
    { id: 'all', label: 'All Events', icon: 'List' },
    { id: 'security', label: 'Security Events', icon: 'Shield' },
    { id: 'admin', label: 'Admin Actions', icon: 'UserCheck' },
    { id: 'payments', label: 'Failed Payments', icon: 'CreditCard' },
    { id: 'system', label: 'System Changes', icon: 'Settings' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <div className="container mx-auto px-4 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
                System Audit Trail Dashboard
              </h1>
              <p className="text-muted-foreground">
                Comprehensive audit logging and compliance monitoring
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={loadAuditData}>
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Refresh
              </Button>
              <Button variant="default" size="sm" onClick={handleExport}>
                <Icon name="Download" size={16} className="mr-2" />
                Export
              </Button>
            </div>
          </div>

          {/* Statistics */}
          {statistics && <AuditStatistics stats={statistics} />}

          {/* Tab Navigation */}
          <div className="bg-card rounded-lg border border-border mb-6">
            <div className="flex items-center gap-2 p-2 overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-250 whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {loading ? (
              <div className="card">
                <div className="flex items-center justify-center py-12">
                  <Icon name="Loader" size={32} className="animate-spin text-primary" />
                  <span className="ml-3 text-muted-foreground">Loading audit data...</span>
                </div>
              </div>
            ) : (
              <>
                {activeTab === 'all' && <AuditLogTable logs={auditLogs} onRefresh={loadAuditData} />}
                {activeTab === 'security' && <SecurityEventsPanel onRefresh={loadAuditData} />}
                {activeTab === 'admin' && <AdminActionsPanel onRefresh={loadAuditData} />}
                {activeTab === 'payments' && <FailedPaymentsPanel onRefresh={loadAuditData} />}
                {activeTab === 'system' && <SystemChangesPanel onRefresh={loadAuditData} />}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemAuditTrailDashboard;